import React, { useMemo } from 'react';

import { getClickAttrs } from '../Analytics/gtag';
import { getFooterNavigation, socialLinks, appStoreLinks } from './constants';
import { useStatsig } from '../hooks/useStatsig';
import config from '../../../config';

import styles from './footer.scss';

const { marketingAppHost } = config;

const Footer = () => {
    const { checkGate, featureGates } = useStatsig();
    const isDirectoryRedirectEnabled = checkGate(featureGates.DIRECTORY_LINKS_REDIRECT);
    const manageCookiesLinkTitle = 'Manage Cookies';
    const footerNavigation = getFooterNavigation({
        redirectToNextApp: isDirectoryRedirectEnabled,
    });

    const copyrightText = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return `© ${currentYear}, UTR Sports`;
    }, []);

    return (
        <div className={styles.footerContainer} data-testid="footer">
            <footer>
                <div className={styles.content}>
                    <div className={styles.images}>
                        <a
                            href={`${marketingAppHost}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...getClickAttrs({
                                category: 'Footer',
                                location: 'Footer Link',
                                context: `${config.brandedUrl}`,
                            })}
                            data-testid="footer.logo-link"
                        >
                            <img
                                src="/footer/utr-sports-logo.svg"
                                alt="utr-sports-logo"
                                className="mb-4"
                                data-testid="footer.logo"
                            />
                        </a>
                        <div className="d-flex justify-content-between mb-4" data-testid="footer.social-links">
                            {socialLinks.map((item, index) => (
                                <a
                                    key={`${item.title}-${index}`}
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    {...getClickAttrs({
                                        category: 'Footer',
                                        location: 'Footer Link',
                                        context: item.title,
                                    })}
                                    data-testid={`footer.social-links.${item.title}`}
                                >
                                    <img src={item.imgSrc} alt={item.alt} />
                                </a>
                            ))}
                        </div>
                        <div className="d-flex justify-content-center" data-testid="footer.app-store-links">
                            {appStoreLinks.map((item, index) => (
                                <a
                                    key={`${item.title}-${index}`}
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    {...getClickAttrs({
                                        category: 'Footer',
                                        location: 'Footer Link',
                                        context: item.title,
                                    })}
                                    data-testid={`footer.app-store-links.${item.title}`}
                                >
                                    <img
                                        alt={item.alt}
                                        src={item.imgSrc}
                                        className={styles.appStoreLogo}
                                    />
                                </a>
                            ))}
                        </div>
                    </div>
                    <div className={styles.navigationContainer} data-testid="footer.navigation">
                        {footerNavigation.map((section, index) => (
                            <div
                                className={styles.navigation}
                                key={`${section.header}-${index}`}
                                data-testid={`footer.navigation.${section.header}`}
                            >
                                <h3 data-testid={`footer.navigation.${section.header}.header`}>{section.header}</h3>
                                {section.links.map((item, index) => (
                                    <a
                                        key={`${item.title}-${index}`}
                                        href={item.href}
                                        target={item.title === manageCookiesLinkTitle ? undefined : '_blank'}
                                        rel={item.title === manageCookiesLinkTitle ? undefined : 'noopener noreferrer'}
                                        {...getClickAttrs({
                                            category: 'Footer',
                                            location: 'Footer Link',
                                            context: item.title,
                                        })}
                                        data-testid={`footer.navigation.${section.header}.link-${item.title}`}
                                    >
                                        {item.title}
                                    </a>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
                <div className={styles.copyright} data-testid="footer.copyright">
                    <p>{copyrightText}</p>
                </div>
            </footer>
        </div>
    );
};

export default Footer;
